import PlayIcon from "@/assets/colorable/play.svg?react";
import TickIcon from "@/assets/colorable/tick.svg?react";
import TimesIcon from "@/assets/colorable/times.svg?react";
import type {AIEvaluationSummarySerializer} from "@/generated/DjangoTypes";
import clsxm from "@/lib/clsxm";
import {acceptLanguageAtom} from "@/store/jotaiAtoms";
import {useAtomValue} from "jotai";
import type {FC} from "react";
import {useTranslation} from "react-i18next";

const AIFeedbackPanel: FC<{
  aiEvaluation: AIEvaluationSummarySerializer;
  onJump?: (slideIndex: number, timestampSeconds?: number) => void;
}> = ({aiEvaluation, onJump}) => {
  const {t} = useTranslation("content_approval");
  const lang = useAtomValue(acceptLanguageAtom);

  const sections = [
    {
      titleKey: "ai_section_brief_compliance" as const,
      checks: (aiEvaluation.check_results.briefing ?? []).map(row => ({
        description: (lang.startsWith("de") ? row.label_de : row.label_en) || row.label || row.id,
        detail: (lang.startsWith("de") ? row.reason_de : row.reason_en) || row.reason,
        passed: row.passed,
        slideIndex: row.item_index !== undefined ? row.item_index - 1 : undefined,
        timestamp: row.timestamp_start || undefined,
      })),
    },
    {
      titleKey: "ai_section_quality_safety" as const,
      checks: (aiEvaluation.check_results.generic ?? []).map(row => ({
        description: (lang.startsWith("de") ? row.label_de : row.label_en) || row.label || row.id,
        detail: (lang.startsWith("de") ? row.reason_de : row.reason_en) || row.reason,
        passed: row.passed,
        slideIndex: row.item_index !== undefined ? row.item_index - 1 : undefined,
        timestamp: row.timestamp_start || undefined,
      })),
    },
  ].filter(s => s.checks.length > 0);

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-4">
      {sections.map(section => {
        const sectionPassed = section.checks.filter(c => c.passed).length;
        return (
          <div key={section.titleKey}>
            <div className="mb-2.5 flex items-center justify-between">
              <span className="text-neutral-black text-xs font-bold tracking-wide">{t(section.titleKey)}</span>
              <span
                className={clsxm(
                  "font-mono text-xs",
                  sectionPassed === section.checks.length ? "text-success-medium-green" : "text-neutral-grey-dark",
                )}>
                {sectionPassed}/{section.checks.length} {t("checks_passed")}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              {section.checks.map((check, i) => (
                <div
                  key={i}
                  className={clsxm(
                    "flex items-start gap-2.5 rounded-lg px-2.5 py-2",
                    check.passed ? "border border-transparent" : "bg-danger-light-red",
                  )}>
                  <div
                    className={clsxm(
                      "mt-0.5 flex size-4.5 shrink-0 items-center justify-center rounded-full",
                      check.passed
                        ? "bg-success-herbal/20 text-success-medium-green"
                        : "bg-danger-raspberry/15 text-danger-dark-red",
                    )}>
                    {check.passed ? <TickIcon className="size-2" /> : <TimesIcon className="size-2" />}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span
                      className={clsxm(
                        "text-sm font-medium",
                        check.passed ? "text-neutral-grey-darkest" : "text-danger-dark-red",
                      )}>
                      {check.description}
                    </span>
                    <span className={clsxm("text-xs", check.passed ? "text-neutral-grey-dark" : "text-amber-700")}>
                      {check.detail}
                    </span>
                    {!check.passed && check.timestamp !== undefined && check.slideIndex !== undefined && onJump && (
                      <button
                        className="border-danger-rubicund mt-1.5 flex w-fit items-center gap-1.5 rounded-md border bg-white px-2.5 py-1 text-xs font-medium"
                        onClick={() => {
                          const [m, s] = check.timestamp!.split(":");
                          const seconds = parseInt(m) * 60 + parseInt(s);
                          if (!isNaN(seconds)) onJump(check.slideIndex!, seconds);
                        }}>
                        <PlayIcon className="text-danger-dark-red h-2 w-1.5 shrink-0" />
                        <span className="text-danger-dark-red">{t("jump_to", {timestamp: check.timestamp})}</span>
                        <span className="text-neutral-grey-dark">
                          · {t("slide")} {check.slideIndex + 1}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AIFeedbackPanel;
